import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp, query, orderBy } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Save, Info, Image as ImageIcon, Tag, DollarSign, Layers } from 'lucide-react';

const CreateProduct = ({ db }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    type: 'parts',
    uom: 'nos',
    brand: 'Nexrise',
    manufacture: 'Nexrise India',
    rate: 0,
    inventoryvalue: 0,
    basesku: '',
    sac: '',
    image: '',
    presetComponents: []
  });

  const categories = ["Frames", "Braces", "Platforms", "Castors", "Ladders", "Couplers", "Pipes", "Accessories"];
  const types = ["parts", "preset", "petty_item", "consumables", "frp-ladder", "alu-ladder"];
  const uoms = ["nos", "set", "kg", "mtr", "sqft"];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (id) {
          const docRef = doc(db, "products", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setFormData({ ...formData, ...docSnap.data() });
          }
        } else if (location.state?.cloneData) {
          const { id: _, ...cloneData } = location.state.cloneData;
          setFormData(prev => ({ 
            ...prev, 
            ...cloneData, 
            sku: `${cloneData.sku}-COPY`,
            name: `${cloneData.name} (Copy)`
          }));
        }
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, location.state]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sku || !formData.name) return alert("SKU and Name are required");

    setIsSaving(true);
    try {
      const finalData = {
        ...formData,
        updatedAt: serverTimestamp(),
        createdAt: formData.createdAt || serverTimestamp()
      };

      if (id) {
        await updateDoc(doc(db, "products", id), finalData);
      } else {
        await addDoc(collection(db, "products"), finalData);
      }
      alert(`Product ${id ? 'updated' : 'created'} successfully!`);
      navigate('/admin/products');
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
          {id ? 'Edit Product' : 'Add New Product'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        
        {/* PRODUCT IDENTITY */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><Info size={18} /> Product Identity</div>
          <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={inputGroup}>
              <label style={labelStyle}>SKU (Part Number)*</label>
              <input type="text" name="sku" value={formData.sku} onChange={handleChange} style={inputStyle} required />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Item Name*</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} style={inputStyle} required />
            </div>
            <div style={{ ...inputGroup, gridColumn: 'span 2' }}>
              <label style={labelStyle}>Description</label>
              <textarea name="description" value={formData.description} onChange={handleChange} style={{ ...inputStyle, minHeight: '80px' }} />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Base SKU</label>
              <input type="text" name="basesku" value={formData.basesku} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>SAC Code</label>
              <input type="text" name="sac" value={formData.sac} onChange={handleChange} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* ATTRIBUTES */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><Tag size={18} /> Categorization & Attributes</div>
          <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div style={inputGroup}>
              <label style={labelStyle}>Category</label>
              <select name="category" value={formData.category} onChange={handleChange} style={inputStyle}>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Type</label>
              <select name="type" value={formData.type} onChange={handleChange} style={inputStyle}>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>UOM</label>
              <select name="uom" value={formData.uom} onChange={handleChange} style={inputStyle}>
                {uoms.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Brand</label>
              <input type="text" name="brand" value={formData.brand} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Manufacture</label>
              <input type="text" name="manufacture" value={formData.manufacture} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Image URL</label>
              <input type="text" name="image" value={formData.image} onChange={handleChange} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* FINANCIALS */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><DollarSign size={18} /> Financials</div>
          <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={inputGroup}>
              <label style={labelStyle}>Rate (₹)</label>
              <input type="number" name="rate" value={formData.rate} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={inputGroup}>
              <label style={labelStyle}>Inventory Value (₹)</label>
              <input type="number" name="inventoryvalue" value={formData.inventoryvalue} onChange={handleChange} style={inputStyle} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => navigate(-1)} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={isSaving} style={primaryBtn}>
            <Save size={18} /> {isSaving ? 'Saving...' : 'Save Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' };
const cardHeaderStyle = { padding: '15px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#fdfdfd', color: '#334155' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px' };
const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#64748b' };
const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', outline: 'none' };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold', cursor: 'pointer' };
const secondaryBtn = { padding: '12px 24px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontWeight: 'bold', cursor: 'pointer' };

export default CreateProduct;